import { current } from 'immer';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';

interface UserResponseState {
  user_responses: any;
  user_responses_loading: boolean;
  user_responses_error: string | null;
  loading: boolean;
  error: string | null;
  fetched_user_responses_for_eu_ids: number[];
}

interface UserResponseActions {
  setParentsPoints: (parentsPoints: any) => void;
  setLocalUserResponse: (response: any, options?: any) => void;
  setLocalUserResponses: (responses: any, options?: any) => void;
  setFetchedUserResponsesForEuIds: (
    ids: number[],
    options?: { merge?: boolean; remove?: boolean }
  ) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

type UserResponseStore = UserResponseState & UserResponseActions;

const initialState: UserResponseState = {
  user_responses: {},
  user_responses_loading: false,
  user_responses_error: null,
  loading: false,
  error: null,
  fetched_user_responses_for_eu_ids: [],
};

export const useUserResponseStore = create<UserResponseStore>()(
  immer((set) => ({
    ...initialState,

    setParentsPoints: (payload: any, options = { merge: true }) =>
      set((state) => {
        const { experience_id, experience_user_id, parents_points } = payload;

        const newUserResponses = state.user_responses;
        // Ensure nested structure exists
        const experienceIdentifier = `eId_${experience_id}`;
        const experienceUserIdentifier = `euId_${experience_user_id}`;

        newUserResponses[experienceIdentifier] ??= {};
        newUserResponses[experienceIdentifier][experienceUserIdentifier] ??= {};

        // Update or create the response at experience user level
        const euResponses = newUserResponses[experienceIdentifier][experienceUserIdentifier];

        console.log('setParentsPoints ==>  parentResponse', current(euResponses));
        if (euResponses) {
          parents_points.map((parent: any) => {
            const parentResponse: any = Object.values(current(euResponses)).find(
              (response: any) => {
                return response.id === parent.id;
              }
            );
            if (parentResponse) {
              const updatedParentResponse = {
                ...parentResponse,
                points: parent.points,
                marked: parent.marked,
              };
              if (parent.submission_criterium_associations_attributes) {
                updatedParentResponse.submission_criterium_associations_attributes =
                  parent.submission_criterium_associations_attributes;
              }
              const segmentIdentifier = `sId_${parentResponse.segment_id}`;
              newUserResponses[experienceIdentifier][experienceUserIdentifier][segmentIdentifier] =
                updatedParentResponse;
              console.log(
                'setParentsPoints ==>  parentResponse',
                parentResponse,
                newUserResponses[experienceIdentifier][experienceUserIdentifier][segmentIdentifier]
              );
            }
          });
        }
        console.log('setParentsPoints ==>  newUserResponses', newUserResponses);
        state.user_responses = newUserResponses;
      }),

    setLocalUserResponse: (response: any, options = { merge: true }) =>
      set((state) => {
        console.log('setLocalUserResponse ==>  response', state.user_responses);

        const { experience_id, experience_user_id, segment_id } = response;

        const newUserResponses = state.user_responses;
        // Ensure nested structure exists
        const experienceIdentifier = `eId_${experience_id}`;
        const experienceUserIdentifier = `euId_${experience_user_id}`;
        const segmentIdentifier = `sId_${segment_id}`;
        newUserResponses[experienceIdentifier] ??= {};
        newUserResponses[experienceIdentifier][experienceUserIdentifier] ??= {};

        // Update or create the response at segment level
        const existingResponse =
          newUserResponses[experienceIdentifier][experienceUserIdentifier][segmentIdentifier];

        newUserResponses[experienceIdentifier][experienceUserIdentifier][segmentIdentifier] =
          options?.merge ? { ...existingResponse, ...response } : response;
        // console.log(
        //   'setLocalUserResponse state.user_responses ==>',
        //   current(
        //     newUserResponses[experienceIdentifier][experienceUserIdentifier][segmentIdentifier]
        //   )
        // );

        state.user_responses = newUserResponses;
      }),

    setLocalUserResponses: (responses: any, options: any = { merge: true }) =>
      set((state) => {
        // console.log('setLocalUserResponses responses ==>', responses);

        // Start with existing responses if merging, otherwise start fresh
        const newResponses = options.merge ? { ...state.user_responses } : {};

        // Process each response
        responses.forEach((responseData: any) => {
          const response = responseData.attributes;
          const { experience_id, experience_user_id, segment_id } = response;

          // Ensure nested structure exists - create new objects to avoid reference sharing
          const experienceIdentifier = `eId_${experience_id}`;
          if (!newResponses[experienceIdentifier]) {
            newResponses[experienceIdentifier] = {};
          }
          const experienceUserIdentifier = `euId_${experience_user_id}`;
          if (!newResponses[experienceIdentifier][experienceUserIdentifier]) {
            newResponses[experienceIdentifier][experienceUserIdentifier] = {};
          }

          const segmentIdentifier = `sId_${segment_id}`;
          // Store the response at segment level
          newResponses[experienceIdentifier][experienceUserIdentifier][segmentIdentifier] =
            response;
        });

        // console.log('setLocalUserResponses newResponses ==>', newResponses);
        state.user_responses = newResponses;
      }),

    setFetchedUserResponsesForEuIds: (eu_ids, options = { merge: true }) =>
      set((state) => {
        if (options.remove) {
          // Remove specified eu_ids from the existing array
          state.fetched_user_responses_for_eu_ids = state.fetched_user_responses_for_eu_ids.filter(
            (id) => !eu_ids.includes(id)
          );
        } else if (options.merge !== false) {
          // Default behavior: merge existing and new eu_ids, then ensure uniqueness using Set
          const combinedIds = [...state.fetched_user_responses_for_eu_ids, ...eu_ids];
          state.fetched_user_responses_for_eu_ids = [...new Set(combinedIds)];
        } else {
          // Replace completely when merge is explicitly false
          state.fetched_user_responses_for_eu_ids = [...new Set(eu_ids)];
        }
      }),

    // Utility setters
    setLoading: (loading) =>
      set((state) => {
        state.loading = loading;
      }),

    setError: (error) =>
      set((state) => {
        state.error = error;
      }),

    reset: () =>
      set((state) => {
        return initialState;
      }),
  }))
);

////////////////////////////////////////////////////////////
// User Responses Selectors
////////////////////////////////////////////////////////////

// Empty array constant to prevent unnecessary re-renders when no results match
const DEFAULT_EMPTY_ARRAY: any[] = [];
const DEFAULT_EMPTY_OBJECT: any = {};

/**
 * Selector to get all user responses
 * @param state - The user response store state
 * @returns All user responses object
 */
export const selectAllUserResponses = (state: UserResponseStore) => {
  return state.user_responses ?? DEFAULT_EMPTY_OBJECT;
};

/**
 * Selector to get user responses loading state
 * @param state - The user response store state
 * @returns User responses loading state
 */
export const selectUserResponsesLoadingState = (state: UserResponseStore) => {
  return state.user_responses_loading;
};

/**
 * Selector to get user responses error
 * @param state - The user response store state
 * @returns User responses error message
 */
export const selectUserResponsesError = (state: UserResponseStore) => {
  return state.user_responses_error;
};

/**
 * Selector to get general loading state
 * @param state - The user response store state
 * @returns General loading state
 */
export const selectUserResponsesLoading = (state: UserResponseStore) => {
  return state.loading;
};

/**
 * Selector to get general error state
 * @param state - The user response store state
 * @returns General error message
 */
export const selectError = (state: UserResponseStore) => {
  return state.error;
};

/**
 * Selector to get all fetched experience user IDs
 * @param state - The user response store state
 * @returns Array of experience user IDs that have been fetched
 */
export const selectFetchedUserResponsesForEuIds = (state: UserResponseStore) => {
  return state.fetched_user_responses_for_eu_ids;
};

/**
 * Selector to get setParentsPoints action
 * @param state - The user response store state
 * @returns setParentsPoints action function
 */
export const selectSetParentsPoints = (state: UserResponseStore) => {
  return state.setParentsPoints;
};

/**
 * Selector to get setLocalUserResponse action
 * @param state - The user response store state
 * @returns setLocalUserResponse action function
 */
export const selectSetLocalUserResponse = (state: UserResponseStore) => {
  return state.setLocalUserResponse;
};

/**
 * Selector to get setLocalUserResponses action
 * @param state - The user response store state
 * @returns setLocalUserResponses action function
 */
export const selectSetLocalUserResponses = (state: UserResponseStore) => {
  return state.setLocalUserResponses;
};

/**
 * Selector to get setFetchedUserResponsesForEuIds action
 * @param state - The user response store state
 * @returns setFetchedUserResponsesForEuIds action function
 */
export const selectSetFetchedUserResponsesForEuIds = (state: UserResponseStore) => {
  return state.setFetchedUserResponsesForEuIds;
};

/**
 * Selector to get setLoading action
 * @param state - The user response store state
 * @returns setLoading action function
 */
export const selectSetLoading = (state: UserResponseStore) => {
  return state.setLoading;
};

/**
 * Selector to get setError action
 * @param state - The user response store state
 * @returns setError action function
 */
export const selectSetError = (state: UserResponseStore) => {
  return state.setError;
};

/**
 * Selector to get reset action
 * @param state - The user response store state
 * @returns reset action function
 */
export const selectReset = (state: UserResponseStore) => {
  return state.reset;
};

/**
 * Selector for user responses based on nested data structure:
 * Data structure: user_responses[experience_id][experience_user_id][segment_id] = response
 *
 * @param state - The user response store state
 * @param params - Filter parameters { experienceId?, userId?, segmentId?, attemptNo?, euId? }
 * @returns Filtered responses based on the specificity of the filters
 */
/**
 * NOTE: Data structure and parameter usage:
 * - experience_id: Should always come from experience_user.experience_id
 * - experience_user_id: Used to support multiple attempts for the same user
 * - This design allows tracking responses across different user attempts within the same experience
 */
export const selectUserResponses = (state: UserResponseStore, params: any) => {
  const { experienceId, experienceUserId, segmentId } = params;
  const allResponses = state.user_responses ?? DEFAULT_EMPTY_OBJECT;
  // console.log('CHECKPRINT => SelectUserResponses', allResponses, params);

  if (!experienceId) return allResponses;

  const experienceIdentifier = `eId_${experienceId}`;
  const experienceResponses = allResponses[experienceIdentifier];
  if (!experienceResponses) return null;

  if (!experienceUserId) return experienceResponses;

  const experienceUserIdentifier = `euId_${experienceUserId}`;
  const userResponses = experienceResponses[experienceUserIdentifier];
  if (!userResponses) return null;

  if (!segmentId) return userResponses;
  const segmentIdentifier = `sId_${segmentId}`;
  const segmentResponse = userResponses[segmentIdentifier];

  // console.log('selectUserResponses segmentResponse ==>', segmentResponse);
  return segmentResponse;
};
